/**
 * Roles de administración de tenant en Vault (menú lateral, pantallas restringidas, etc.).
 * Usa la misma normalización que `AuthService.hasRole` para que cookie/sesión y `user_data` se evalúen igual.
 *
 * `user_data` en localStorage suele ser `extractSafeUserData` (solo `roles[]` con pocos campos).
 * El **access_token** puede traer `authorities` / `realm_access.roles` / `User` con más detalle: también lo leemos.
 *
 * Rol operativo Vault (consulta, archivista, …): `vault_operative_role` desde `GET /api/auth/me`.
 * No confundir con ROLE_DEVELOPER de Simappe (solo OAuth).
 */
import {
  isSimappeTechnicalRoleToken,
  isVaultOperativeScopeHydrated,
  readVaultDashboardRole,
  readVaultIsSuperadminScope,
  readVaultOperativeRole,
  resolveSuperadminFromOperativeScope,
  vaultOperativeRoleLabel,
} from './vault-operative-role.util';
import {
  resolveVaultActiveTenantId,
  vaultResolveActiveTenantContext,
} from './vault-active-tenant.util';
import { isVaultUuid, readVaultTenantIdFromAssignmentRow } from './vault-tenant-scope.util';
import { sessionUserIsCanonicalVaultSuperadmin } from './vault-canonical-superadmin.util';
export const VAULT_TENANT_ADMIN_NORMALIZED_ROLES = new Set([
  'superadmin',
  'super_admin',
  /** Variantes frecuentes en backend / UI en español */
  'super_administrador',
  'administrador',
  'administrador_global',
  'admin',
  'admin_empresa',
  'platform_admin',
]);

export function normalizeVaultRoleToken(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^role_/, '')
    .replace(/[\s-]+/g, '_');
}

export function isTruthySuperadminFlag(value: unknown): boolean {
  if (value === true) return true;
  if (value === 1) return true;
  if (typeof value === 'string') {
    const s = value.toLowerCase().trim();
    return s === 'true' || s === '1' || s === 'yes';
  }
  return false;
}

const ROLE_OBJECT_STRING_KEYS = [
  'name',
  'authority',
  'code',
  'roleName',
  'label',
  'type',
  'key',
  'description',
  'title',
] as const;

/** Tokens de rol extraídos de objetos de rol (Spring/JWT/API varían campos). */
export function collectRoleTokensFromRoleObjects(roles: unknown): string[] {
  const out: string[] = [];
  if (!Array.isArray(roles)) return out;
  for (const r of roles) {
    if (typeof r === 'string' && r.trim()) {
      out.push(r);
      continue;
    }
    if (r && typeof r === 'object') {
      const o = r as Record<string, unknown>;
      for (const k of ROLE_OBJECT_STRING_KEYS) {
        const v = o[k];
        if (typeof v === 'string' && v.trim()) out.push(v);
      }
    }
  }
  return out;
}

export function collectRoleStringsFromUserRecord(u: Record<string, unknown>): string[] {
  const out: string[] = [];
  const single = u['role'];
  if (typeof single === 'string' && single.trim()) out.push(single);
  out.push(...collectRoleTokensFromRoleObjects(u['roles']));

  const authorities = u['authorities'];
  if (Array.isArray(authorities)) {
    for (const a of authorities) {
      if (typeof a === 'string' && a.trim()) out.push(a);
    }
  }

  return out;
}

/** Decodifica payload del JWT en `localStorage` (sin verificar firma; solo claims de UI). */
export function decodeAccessTokenPayload(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem('access_token');
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function pushString(out: string[], v: unknown): void {
  if (typeof v === 'string' && v.trim()) out.push(v);
}

/** Cadenas de rol/autoridad desde claims típicos del JWT (Keycloak/Spring/custom). */
export function collectRoleStringsFromJwtPayload(payload: Record<string, unknown>): string[] {
  const out: string[] = [];
  pushString(out, payload['role']);

  const authorities = payload['authorities'];
  if (Array.isArray(authorities)) {
    for (const a of authorities) pushString(out, a);
  }

  const roles = payload['roles'];
  if (Array.isArray(roles)) {
    for (const r of roles) {
      if (typeof r === 'string') pushString(out, r);
      else if (r && typeof r === 'object') out.push(...collectRoleTokensFromRoleObjects([r]));
    }
  }

  const realm = payload['realm_access'];
  if (realm && typeof realm === 'object') {
    const ra = (realm as Record<string, unknown>)['roles'];
    if (Array.isArray(ra)) {
      for (const r of ra) pushString(out, r);
    }
  }

  const user = payload['User'] ?? payload['user'];
  if (user && typeof user === 'object') {
    out.push(...collectRoleStringsFromUserRecord(user as Record<string, unknown>));
  }

  return out;
}

export function readVaultUserDataFromLocalStorage(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Cookie `user_profile` (Simappe) puede incluir campos que `user_data` no persiste. */
export function readVaultUserProfileFromCookie(): Record<string, unknown> | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)user_profile=([^;]*)/);
  if (!match?.[1]) return null;
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(match[1]));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Registros de usuario usados para flags/roles: localStorage, cookie perfil y claims JWT. */
export function collectVaultSessionUserRecords(): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = [];
  const u = readVaultUserDataFromLocalStorage();
  if (u) records.push(u);
  const profile = readVaultUserProfileFromCookie();
  if (profile) records.push(profile);
  const payload = decodeAccessTokenPayload();
  if (payload) {
    records.push(payload);
    const nested = payload['User'] ?? payload['user'];
    if (nested && typeof nested === 'object') {
      records.push(nested as Record<string, unknown>);
    }
  }
  return records;
}

export function userRecordIndicatesSuperadmin(u: Record<string, unknown>): boolean {
  return (
    isTruthySuperadminFlag(u['is_superadmin']) ||
    isTruthySuperadminFlag(u['superadmin']) ||
    isTruthySuperadminFlag(u['isSuperAdmin']) ||
    isTruthySuperadminFlag(u['is_super_admin']) ||
    isTruthySuperadminFlag(u['superAdmin'])
  );
}

/** Superadmin según JWT (no se altera al fijar rol operativo Vault en localStorage). */
export function sessionJwtIndicatesVaultSuperadmin(): boolean {
  if (sessionUserIsCanonicalVaultSuperadmin()) return true;
  const payload = decodeAccessTokenPayload();
  if (!payload) return false;
  if (userRecordIndicatesSuperadmin(payload)) return true;
  for (const key of ['User', 'user'] as const) {
    const nested = payload[key];
    if (
      nested &&
      typeof nested === 'object' &&
      userRecordIndicatesSuperadmin(nested as Record<string, unknown>)
    ) {
      return true;
    }
  }
  return false;
}

/** Coincide con la lista explícita o con nombres compuestos tipo `tenant_superadmin`. */
export function normalizedRoleImpliesVaultTenantAdmin(norm: string): boolean {
  if (VAULT_TENANT_ADMIN_NORMALIZED_ROLES.has(norm)) return true;
  if (norm.includes('superadmin')) return true;
  if (norm.includes('super_admin')) return true;
  return false;
}

/**
 * Rol tal como lo devuelve `GET /api/dashboard/stats` (`DashboardStatsResponse.role`).
 * Suele coincidir con lo que muestra el tablero ("Superadmin") aunque `user_data` sea mínimo.
 */
export function dashboardRoleStringImpliesVaultTenantAdmin(role: string | null | undefined): boolean {
  if (typeof role !== 'string' || !role.trim()) return false;
  const norm = normalizeVaultRoleToken(role);
  return normalizedRoleImpliesVaultTenantAdmin(norm);
}

/** Solo superadmin global (no admin_empresa ni otros roles tenant-admin). */
export function dashboardRoleIndicatesVaultSuperadmin(role: string | null | undefined): boolean {
  if (typeof role !== 'string' || !role.trim()) return false;
  const norm = normalizeVaultRoleToken(role);
  return (
    norm === 'superadmin' ||
    norm.includes('superadmin') ||
    norm.includes('super_admin') ||
    norm === 'superadministrador'
  );
}

export function userRecordHasAnyNormalizedRole(
  u: Record<string, unknown>,
  allowed: ReadonlySet<string>,
): boolean {
  for (const s of collectRoleStringsFromUserRecord(u)) {
    if (allowed.has(normalizeVaultRoleToken(s))) return true;
  }
  return false;
}

function sessionRecordImpliesVaultTenantAdmin(rec: Record<string, unknown>): boolean {
  if (userRecordIndicatesSuperadmin(rec)) return true;
  for (const s of collectRoleStringsFromUserRecord(rec)) {
    const norm = normalizeVaultRoleToken(s);
    if (VAULT_TENANT_ADMIN_NORMALIZED_ROLES.has(norm) || normalizedRoleImpliesVaultTenantAdmin(norm)) {
      return true;
    }
  }
  return false;
}

/**
 * Administración de tenant: `user_data` en localStorage **y** claims del `access_token`.
 */
export function vaultUserCanTenantAdminFromLocalStorage(): boolean {
  const vaultRole = readVaultOperativeRole();
  if (isVaultOperativeScopeHydrated() || vaultRole) {
    if (resolveSuperadminFromOperativeScope() === true) return true;
    if (vaultRole) {
      const norm = normalizeVaultRoleToken(vaultRole);
      return norm === 'admin_empresa';
    }
    return false;
  }

  for (const rec of collectVaultSessionUserRecords()) {
    if (sessionRecordImpliesVaultTenantAdmin(rec)) return true;
  }
  const payload = decodeAccessTokenPayload();
  if (!payload) return false;
  for (const s of collectRoleStringsFromJwtPayload(payload)) {
    const norm = normalizeVaultRoleToken(s);
    if (VAULT_TENANT_ADMIN_NORMALIZED_ROLES.has(norm) || normalizedRoleImpliesVaultTenantAdmin(norm)) {
      return true;
    }
  }
  return false;
}

/** Misma regla que `vaultUserCanTenantAdminFromLocalStorage`; nombre histórico para el módulo de mensajes. */
export function userCanManagePreservationMessages(): boolean {
  return vaultUserCanTenantAdminFromLocalStorage();
}

/** Roles desde `user_data` y claims del JWT (`access_token`). */
export function collectVaultSessionRoleTokens(): string[] {
  const out: string[] = [];
  const u = readVaultUserDataFromLocalStorage();
  if (u) out.push(...collectRoleStringsFromUserRecord(u));
  const payload = decodeAccessTokenPayload();
  if (payload) out.push(...collectRoleStringsFromJwtPayload(payload));
  return out;
}

export function vaultUserIsSuperadminFromSession(): boolean {
  if (sessionUserIsCanonicalVaultSuperadmin()) return true;

  const fromOperative = resolveSuperadminFromOperativeScope();
  if (fromOperative !== null) return fromOperative;

  if (dashboardRoleIndicatesVaultSuperadmin(readVaultDashboardRole())) return true;

  for (const rec of collectVaultSessionUserRecords()) {
    if (userRecordIndicatesSuperadmin(rec)) return true;
  }

  if (
    collectVaultSessionRoleTokens().some((s) => {
      const norm = normalizeVaultRoleToken(s);
      return norm.includes('superadmin') || norm.includes('super_admin');
    })
  ) {
    return true;
  }

  return false;
}

export function vaultUserIsAdminEmpresaFromSession(): boolean {
  if (vaultUserIsSuperadminFromSession()) return false;
  const vaultRole = readVaultOperativeRole();
  if (isVaultOperativeScopeHydrated() || vaultRole) {
    return vaultRole ? normalizeVaultRoleToken(vaultRole) === 'admin_empresa' : false;
  }
  return collectVaultSessionRoleTokens().some(
    (s) => normalizeVaultRoleToken(s) === 'admin_empresa',
  );
}

/** @deprecated Usar `vaultUserIsSuperadminFromSession`. */
export const vaultUserIsSuperadminFromLocalStorage = vaultUserIsSuperadminFromSession;

export function vaultCurrentUserIdFromSession(): string {
  const u = readVaultUserDataFromLocalStorage();
  if (u) {
    for (const key of ['vault_user_id', 'id', 'user_id'] as const) {
      const value = u[key];
      if (typeof value === 'string' && isVaultUuid(value)) {
        return value.trim();
      }
    }
  }

  const payload = decodeAccessTokenPayload();
  if (payload) {
    for (const key of ['user_id', 'id'] as const) {
      const value = payload[key];
      if (typeof value === 'string' && isVaultUuid(value)) {
        return value.trim();
      }
    }
    const sub = payload['sub'];
    if (typeof sub === 'string' && isVaultUuid(sub)) {
      return sub.trim();
    }
  }

  if (u) {
    const fallback = u['id'] ?? u['user_id'] ?? u['sub'];
    return typeof fallback === 'string' ? fallback.trim() : '';
  }

  if (payload) {
    const sub = payload['sub'];
    return typeof sub === 'string' ? sub.trim() : '';
  }

  return '';
}

/**
 * Tenant activo: delega en {@link resolveVaultActiveTenantId} (orden canónico JWT → localStorage → único tenant).
 */
export function vaultCurrentTenantIdFromSession(): string {
  return resolveVaultActiveTenantId();
}

export type VaultAdminUsersActor = 'superadmin' | 'admin_empresa' | 'denied';

export function vaultResolveAdminUsersActorFromSession(): VaultAdminUsersActor {
  if (sessionUserIsCanonicalVaultSuperadmin()) return 'superadmin';

  const fromOperative = resolveSuperadminFromOperativeScope();
  if (fromOperative === true) return 'superadmin';

  if (readVaultIsSuperadminScope() === false || fromOperative === false) {
    if (vaultUserIsAdminEmpresaFromSession()) return 'admin_empresa';
    return 'denied';
  }

  if (vaultUserIsSuperadminFromSession()) return 'superadmin';
  if (vaultUserIsAdminEmpresaFromSession()) return 'admin_empresa';

  const vaultRole = readVaultOperativeRole();
  if (vaultRole) return 'denied';

  if (vaultUserCanTenantAdminFromLocalStorage()) return 'superadmin';
  return 'denied';
}

/** Rol mostrado en `GET /api/dashboard/stats` → actor admin (Gestión de Usuarios). */
export function vaultResolveAdminUsersActorFromDashboard(
  role: string | null | undefined,
): VaultAdminUsersActor | null {
  if (typeof role !== 'string' || !role.trim()) return null;
  const norm = role.trim().toLowerCase();

  if (
    norm.includes('superadmin') ||
    norm.includes('super admin') ||
    norm.includes('super_admin') ||
    norm === 'superadministrador'
  ) {
    return 'superadmin';
  }

  if (norm.includes('admin') && norm.includes('empresa')) {
    return 'admin_empresa';
  }

  if (dashboardRoleStringImpliesVaultTenantAdmin(role)) {
    // Respetamos únicamente la restricción estricta de `admin_empresa`.
    // Si no es `admin_empresa`, lo tratamos como `superadmin`.
    if (vaultUserIsAdminEmpresaFromSession()) return 'admin_empresa';
    return 'superadmin';
  }

  return null;
}

/** Sesión (JWT + user_data) y, si hace falta, rol del dashboard. */
export function vaultResolveAdminUsersActor(dashboardRole?: string | null): VaultAdminUsersActor {
  const fromSession = vaultResolveAdminUsersActorFromSession();
  if (fromSession !== 'denied') return fromSession;
  if (isVaultOperativeScopeHydrated()) return 'denied';
  return vaultResolveAdminUsersActorFromDashboard(dashboardRole) ?? 'denied';
}

export function vaultResolveIsSuperadmin(dashboardRole?: string | null): boolean {
  return vaultResolveAdminUsersActor(dashboardRole) === 'superadmin';
}

export interface VaultTenantRoleAssignment {
  tenant_id: string;
  tenant_name?: string;
  role: string;
}

export interface VaultSessionRoleDisplay {
  primaryLabel: string;
  detailLines: string[];
  rawRole: string;
}

function readTenantAssignmentsFromUserRecord(
  u: Record<string, unknown>,
): VaultTenantRoleAssignment[] {
  const tenants = u['tenants'];
  if (!Array.isArray(tenants)) return [];
  const out: VaultTenantRoleAssignment[] = [];
  for (const t of tenants) {
    if (!t || typeof t !== 'object') continue;
    const o = t as Record<string, unknown>;
    const tenant_id = readVaultTenantIdFromAssignmentRow(o);
    const role = String(o['role'] ?? '').trim();
    const tenant_name =
      o['tenant_name'] != null
        ? String(o['tenant_name'])
        : o['name'] != null
          ? String(o['name'])
          : undefined;
    if (tenant_id || role) out.push({ tenant_id, tenant_name, role });
  }
  return out;
}

function sessionRoleCompanyDetailLines(dashboardRole?: string | null): string[] {
  const ctx = vaultResolveActiveTenantContext({ dashboardRole });
  if (ctx.isGlobalScope) return [];
  const label = (ctx.companyLabel || ctx.tenantName || '').trim();
  if (!label || label === 'Sin empresa') return [];
  return [`Empresa: ${label}`];
}

/**
 * Rol mostrado en Configuración: prioridad tenant activo en sesión, luego dashboard/API, luego JWT.
 * Paridad con legacy `getCurrentUserRole()` + nombre legible.
 */
export function vaultResolveSessionRoleDisplay(opts?: {
  dashboardRole?: string | null;
  tenants?: VaultTenantRoleAssignment[];
  isSuperadmin?: boolean;
}): VaultSessionRoleDisplay {
  if (sessionUserIsCanonicalVaultSuperadmin()) {
    return {
      primaryLabel: 'Super Administrador',
      detailLines: ['Acceso completo al sistema'],
      rawRole: 'superadmin',
    };
  }

  const vaultScopeSuper = readVaultIsSuperadminScope();
  const vaultOperativeRole = readVaultOperativeRole();

  if (
    vaultScopeSuper === true ||
    (vaultScopeSuper === null && (opts?.isSuperadmin ?? vaultUserIsSuperadminFromSession()))
  ) {
    return {
      primaryLabel: 'Super Administrador',
      detailLines: ['Acceso completo al sistema'],
      rawRole: 'superadmin',
    };
  }

  if (vaultOperativeRole) {
    return {
      primaryLabel: vaultOperativeRoleLabel(vaultOperativeRole),
      detailLines: sessionRoleCompanyDetailLines(vaultOperativeRole),
      rawRole: vaultOperativeRole,
    };
  }

  const dashboardRole = opts?.dashboardRole?.trim();
  if (dashboardRole) {
    return {
      primaryLabel: vaultOperativeRoleLabel(dashboardRole),
      detailLines: sessionRoleCompanyDetailLines(dashboardRole),
      rawRole: dashboardRole,
    };
  }

  const tenantId = vaultCurrentTenantIdFromSession();
  const assignmentSources: VaultTenantRoleAssignment[] = [];

  if (opts?.tenants?.length) {
    assignmentSources.push(...opts.tenants);
  }
  for (const rec of collectVaultSessionUserRecords()) {
    assignmentSources.push(...readTenantAssignmentsFromUserRecord(rec));
  }

  const withRole = assignmentSources.filter((a) => a.role?.trim());
  if (withRole.length) {
    const match =
      (tenantId ? withRole.find((a) => a.tenant_id === tenantId) : undefined) ?? withRole[0];
    if (match?.role?.trim()) {
      const detailLines: string[] = [];
      const company = (match.tenant_name ?? '').trim() || match.tenant_id;
      if (company) detailLines.push(`Empresa: ${company}`);
      return {
        primaryLabel: vaultOperativeRoleLabel(match.role),
        detailLines,
        rawRole: match.role,
      };
    }
  }

  const tokens = collectVaultSessionRoleTokens().filter(
    (t) => !isSimappeTechnicalRoleToken(t),
  );
  const priority = ['admin_empresa', 'archivista', 'auditor', 'consulta'];
  for (const p of priority) {
    const hit = tokens.find((t) => {
      const norm = normalizeVaultRoleToken(t);
      return norm === p || norm.includes(p);
    });
    if (hit) {
      return {
        primaryLabel: vaultOperativeRoleLabel(hit),
        detailLines: [],
        rawRole: hit,
      };
    }
  }

  const first = tokens.find((t) => t.trim());
  if (first) {
    return {
      primaryLabel: vaultOperativeRoleLabel(first),
      detailLines: [],
      rawRole: first,
    };
  }

  return {
    primaryLabel: 'Usuario',
    detailLines: [],
    rawRole: '',
  };
}
